export const commercialImportDatasets = [
  {
    code: 'CUSTOMERS',
    label: 'Customers',
    manualOnly: false,
    required: ['name'],
    fields: [
      { key: 'externalId', label: 'External ID', aliases: ['external id', 'externalid', 'customer id', 'client id', 'id'] },
      { key: 'name', label: 'Customer name', required: true, aliases: ['name', 'customer', 'customer name', 'client', 'client name', 'company', 'empresa', 'cliente'] },
      { key: 'country', label: 'Country', aliases: ['country', 'pais', 'country code'] },
      { key: 'status', label: 'Status', aliases: ['status', 'state', 'customer status', 'estado'] },
      { key: 'segment', label: 'Segment', aliases: ['segment', 'customer segment'] },
      { key: 'industry', label: 'Industry', aliases: ['industry', 'sector'] },
      { key: 'leadSource', label: 'Lead source', aliases: ['lead source', 'leadsource', 'source'] },
      { key: 'notesSummary', label: 'Notes', aliases: ['notes', 'notes summary', 'summary'] },
    ],
  },
  {
    code: 'OPPORTUNITIES',
    label: 'Opportunities',
    manualOnly: false,
    required: ['company'],
    fields: [
      { key: 'opportunityId', label: 'Bcome opportunity ID', aliases: ['opportunity id', 'bcome opportunity id'] },
      { key: 'externalId', label: 'External deal ID', aliases: ['external id', 'deal id', 'crm id'] },
      { key: 'company', label: 'Company', required: true, aliases: ['company', 'customer', 'client', 'company name', 'empresa', 'cliente'] },
      { key: 'stage', label: 'Pipeline stage', aliases: ['stage', 'pipeline stage', 'pipeline', 'etapa'] },
      { key: 'arrPotential', label: 'Potential ARR', aliases: ['arr potential', 'arrpotential', 'potential arr', 'arr', 'arr est.', 'arr est. (\u20ac)', 'arr (\u20ac)', 'value'] },
      { key: 'probability', label: 'Probability', aliases: ['probability', 'likelihood', 'chance'] },
      { key: 'contactName', label: 'Contact name', aliases: ['contact name', 'contact', 'point of contact', 'contacto'] },
      { key: 'contactEmail', label: 'Contact email', aliases: ['contact email', 'email', 'e-mail'] },
      { key: 'contactPhone', label: 'Contact phone', aliases: ['contact phone', 'phone', 'telephone'] },
      { key: 'expectedCloseDate', label: 'Expected close date', aliases: ['expected close date', 'close date'] },
      { key: 'nextAction', label: 'Next action', aliases: ['next action', 'nextaction', 'prox. accion', 'proxima accion'] },
      { key: 'nextActionAt', label: 'Next action date', aliases: ['next action date', 'next action at', 'nextactionat'] },
      { key: 'notes', label: 'Notes', aliases: ['notes', 'comments', 'notas'] },
      { key: 'leadSource', label: 'Lead source', aliases: ['lead source', 'leadsource', 'source'] },
      { key: 'industry', label: 'Industry', aliases: ['industry', 'sector'] },
      { key: 'forecastCategory', label: 'Forecast category', aliases: ['forecast category', 'forecast'] },
    ],
  },
];

export const deferredCommercialImports = [
  { label: 'Renewals and agreements', destination: 'Manual entry', reason: 'Terms, payment stages, recognition dates and MRR effects need an operator review.', href: '#/commercial-entry' },
  { label: 'MRR movements', destination: 'Manual entry', reason: 'MRR changes must be applied through the agreement or manual revenue workflow so calculations remain traceable.', href: '#/commercial-entry' },
  { label: 'Costs and acquisition spend', destination: 'Manual entry', reason: 'Use Manual entry while the import contract for month, currency and allocation is defined.', href: '#/commercial-entry' },
  { label: 'Contracts and subscriptions', destination: 'Agreements', reason: 'Use Agreements for commercial terms; imported legacy contract rows are not yet safe to merge.', href: '#/commercial-renewals' },
];

const normalizeHeader = value => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase()
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ');

export const importDataset = code => commercialImportDatasets.find(dataset => dataset.code === code) || commercialImportDatasets[0];

export const autoMapCommercialColumns = (columns = [], datasetCode) => {
  const dataset = importDataset(datasetCode);
  const normalizedColumns = columns.map(column => ({ column, normalized: normalizeHeader(column) }));
  return dataset.fields.reduce((mapping, field) => {
    const aliases = [field.key, field.label, ...field.aliases].map(normalizeHeader);
    const match = normalizedColumns.find(column => aliases.includes(column.normalized));
    return match ? { ...mapping, [field.key]: match.column } : mapping;
  }, {});
};

const headerAliases = datasetCode => importDataset(datasetCode).fields.map(field => ({
  required: Boolean(field.required),
  aliases: [field.key, field.label, ...field.aliases].map(normalizeHeader),
}));

export const detectCommercialHeaderRow = (matrix = [], datasetCode) => {
  const definitions = headerAliases(datasetCode);
  const candidates = matrix.slice(0, 20);
  let best = { index: 0, score: -1 };
  candidates.forEach((row, index) => {
    const cells = Array.isArray(row) ? row.map(normalizeHeader).filter(Boolean) : [];
    const matched = definitions.filter(field => field.aliases.some(alias => cells.includes(alias)));
    const required = matched.filter(field => field.required).length;
    const score = required * 100 + matched.length * 20 + Math.min(cells.length, 10);
    if (score > best.score) best = { index, score };
  });
  return best.index + 1;
};

export const parseCommercialSheetMatrix = (matrix = [], datasetCode, requestedHeaderRow) => {
  const detected = detectCommercialHeaderRow(matrix, datasetCode);
  const headerRow = Math.max(1, Math.min(matrix.length || 1, Number(requestedHeaderRow) || detected));
  const header = Array.isArray(matrix[headerRow - 1]) ? matrix[headerRow - 1] : [];
  const seen = new Map();
  const positions = header.map((value, index) => {
    const label = String(value ?? '').trim();
    if (!label) return null;
    const count = (seen.get(label) || 0) + 1;
    seen.set(label, count);
    return { index, label: count === 1 ? label : `${label} (${count})` };
  }).filter(Boolean);
  const rows = matrix.slice(headerRow).map((source, index) => ({
    __sourceRow: headerRow + index + 1,
    ...Object.fromEntries(
      positions.map(position => [position.label, Array.isArray(source) ? source[position.index] ?? '' : '']),
    ),
  })).filter(row => Object.entries(row).some(([key, value]) => (
    key !== '__sourceRow' && String(value ?? '').trim()
  )));
  return { headerRow, detectedHeaderRow: detected, columns: positions.map(position => position.label), rows };
};

export const missingRequiredImportFields = (datasetCode, mapping = {}) => importDataset(datasetCode)
  .required
  .filter(field => !String(mapping[field] || '').trim());

export const importSourceForFile = filename => String(filename || '').toLowerCase().endsWith('.csv') ? 'CSV' : 'EXCEL';

export const mappedCommercialRows = (rows = [], mapping = {}, headerRow = 1) => rows.map((row, index) => ({
  sourceRow: Number(row?.__sourceRow) || Number(headerRow || 1) + index + 1,
  ...Object.fromEntries(Object.entries(mapping).map(([field, column]) => [field, row?.[column] ?? ''])),
})).filter(row => Object.entries(row).some(([field, value]) => (
  field !== 'sourceRow' && String(value ?? '').trim()
))).slice(0, 25);

export const buildCommercialImportRequest = ({
  dataset, filename, sheet, headerRow = 1, mapping, rows, expectedFingerprint,
}) => ({
  dataset,
  source: importSourceForFile(filename),
  filename,
  sheet,
  headerRow,
  mapping,
  rows,
  ...(expectedFingerprint ? { expectedFingerprint } : {}),
});
